import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPivotPointEntryConditions } from '../../api/tradeGroups';
import { useAutoRefreshTrigger } from '../../stores/useGlobalAutoRefreshStore';
import './PivotPointExitConditionsBar.css';

/**
 * PivotPointExitConditionsBar (UI-PPEC-001).
 *
 * Proximity-to-target exit readout for an in-position Pivot Point group: distance from the current
 * price to the stop-loss and the three take-profit tiers (PP / R1 / R2). Mirrors the sibling
 * proximity exit-bar pattern (BollingerExitConditionsBar) and hides a tier's target once that tier
 * has filled. Shares the entry bar's queryKey so both bars are served by one HTTP call.
 *
 * The non-price exits (time-limit, regime-turned-bear) are intentionally NOT proximity segments —
 * they have no distance to render (spec Out of Scope).
 */
export interface PivotPointExitConditionsBarProps {
  tradeGroupId: string;
  className?: string;
}

const STALE_TIME_MS = 30_000;

const formatPrice = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  if (Math.abs(value) < 1) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
};

const formatPct = (pct: number | null): string => {
  if (pct === null) return '—';
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
};

const distancePct = (target: number | null | undefined, current: number | null | undefined): number | null => {
  if (target === null || target === undefined || current === null || current === undefined || current === 0) {
    return null;
  }
  return ((target - current) / current) * 100;
};

/** Target proximity: at (≤0.5%), near (≤2%), far otherwise. */
const targetStatus = (pct: number | null): { cls: string; symbol: string } => {
  if (pct === null) return { cls: 'far', symbol: '◆' };
  const abs = Math.abs(pct);
  if (abs <= 0.5) return { cls: 'at', symbol: '✓' };
  if (abs <= 2) return { cls: 'near', symbol: '◉' };
  return { cls: 'far', symbol: '◆' };
};

/** Stop proximity: critical when price is within 2% of the stop. */
const stopStatus = (pct: number | null): { cls: string; symbol: string } => {
  if (pct === null) return { cls: 'far', symbol: '◆' };
  if (Math.abs(pct) <= 2) return { cls: 'critical', symbol: '⚠' };
  return { cls: 'far', symbol: '◆' };
};

interface Segment {
  key: string;
  label: string;
  price: number | null | undefined;
  pct: number | null;
  cls: string;
  symbol: string;
  isTarget: boolean;
}

const PivotPointExitConditionsBar: React.FC<PivotPointExitConditionsBarProps> = ({
  tradeGroupId,
  className = '',
}) => {
  const refreshTrigger = useAutoRefreshTrigger('trade-groups');

  // SAME queryKey as PivotPointEntryConditionsBar → TanStack dedupes to one HTTP call.
  const { data, isLoading, isError } = useQuery({
    queryKey: ['pivot-entry-conditions', tradeGroupId, refreshTrigger],
    queryFn: () => getPivotPointEntryConditions(tradeGroupId),
    staleTime: STALE_TIME_MS,
    retry: 1,
  });

  if (isError) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={`pivot-exit-conditions-bar loading ${className}`}>
        <div className="pivot-exit-gauge" />
      </div>
    );
  }

  if (!data || !data.in_position) {
    return null;
  }

  const {
    current_price,
    stop_loss_price,
    target_pivot,
    target_r1,
    target_r2,
    partial_exit_completed,
    second_partial_completed,
  } = data;

  const stopPct = distancePct(stop_loss_price, current_price);
  const stopStat = stopStatus(stopPct);

  const segments: Segment[] = [
    {
      key: 'stop', label: 'Stop', price: stop_loss_price, pct: stopPct,
      cls: stopStat.cls, symbol: stopStat.symbol, isTarget: false,
    },
  ];

  // PP tier hidden once the tier-1 partial has filled; R1 hidden after tier-2.
  if (!partial_exit_completed) {
    const pct = distancePct(target_pivot, current_price);
    const s = targetStatus(pct);
    segments.push({ key: 'pp', label: 'PP', price: target_pivot, pct, cls: s.cls, symbol: s.symbol, isTarget: true });
  }
  if (!second_partial_completed) {
    const pct = distancePct(target_r1, current_price);
    const s = targetStatus(pct);
    segments.push({ key: 'r1', label: 'R1', price: target_r1, pct, cls: s.cls, symbol: s.symbol, isTarget: true });
  }
  {
    const pct = distancePct(target_r2, current_price);
    const s = targetStatus(pct);
    segments.push({ key: 'r2', label: 'R2', price: target_r2, pct, cls: s.cls, symbol: s.symbol, isTarget: true });
  }

  const tiersFilled = (partial_exit_completed ? 1 : 0) + (second_partial_completed ? 1 : 0);

  // Nearest upcoming take-profit target (above current price, still open).
  const nearest = segments
    .filter(s => s.isTarget && s.pct !== null && s.pct >= 0)
    .sort((a, b) => (a.pct as number) - (b.pct as number))[0];

  return (
    <div className={`pivot-exit-conditions-bar ${className}`}>
      <div className="pivot-exit-header-row">
        <span className="pivot-exit-label">Exit Conditions:</span>
        <span className="pivot-exit-summary" role="status" aria-label={`${tiersFilled} of 3 tiers taken`}>
          {tiersFilled}/3 tiers taken
        </span>
        {nearest && (
          <span className="pivot-exit-nearest" title="Nearest open take-profit target">
            nearest {nearest.label} {formatPct(nearest.pct)}
          </span>
        )}
      </div>

      <div className="pivot-exit-gauge" role="img" aria-label="Pivot exit proximity — stop and take-profit targets">
        {segments.map(seg => (
          <div
            key={seg.key}
            className={`pivot-exit-segment ${seg.cls}`}
            title={`${seg.label} ${formatPrice(seg.price)} (${formatPct(seg.pct)})`}
            aria-label={`${seg.label} at ${formatPrice(seg.price)}, ${formatPct(seg.pct)} from price`}
          >
            <span className="pivot-exit-segment-symbol">{seg.symbol}</span>
            <span className="pivot-exit-segment-label">{seg.label}</span>
            <span className="pivot-exit-segment-pct">{formatPct(seg.pct)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PivotPointExitConditionsBar;
